import { Button, Card, Col, Container, Row, Stack } from "react-bootstrap";
import { DynamicText } from "@/ui/DynamicText";
import JobSearch from "@sourceflow-uk/job-search";
import "@sourceflow-uk/job-search/dist/bundle.css";
import { trimText } from "@/functions/trimText";
import { useLocale } from "@/hooks/useLocale";
import { placeholders } from "@/data/placeholders";
import classes from "./styles.module.scss";
import clsx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLink } from "@/hooks/useLink";
import Reset from "@/assets/Reset.svg";
import jobsSectors from "@/../.sourceflow/jobs-Sectors.json";
import jobsType from "@/../.sourceflow/jobs-Type.json";
import jobsWorkingEnvironment from "@/../.sourceflow/jobs-Working Environment.json";
import CaretDown from "@/assets/CaretDown.svg";

export const JobsPageFeedSection = ({ className }) => {
  const [seconds, setSeconds] = useState(5);
  const locale = useLocale();
  const link = useLink();
  const insertRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [allowRedirect, setAllowRedirect] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      if (params.has("redirect") && params.get("redirect") === "0") {
        setAllowRedirect(false);
      }
    }
  }, []);

  useEffect(() => {
    const wrapper = document.getElementsByClassName("js-form-input-wrapper").item(0);
    if (wrapper && insertRef.current === false) {
      const input1 = wrapper.children[0];
      const input2 = wrapper.children[1];
      const input3 = wrapper.children[2];
      const button = wrapper.children[3];
      const div1 = document.createElement("div");
      const div2 = document.createElement("div");
      const div3 = document.createElement("div");
      const label1 = document.createElement("label");
      const label2 = document.createElement("label");
      const label3 = document.createElement("label");
      label1.innerText = placeholders[`jobs.search.query.label.${locale}`];
      label2.innerText = placeholders[`jobs.search.location.label.${locale}`];
      label3.innerText = placeholders[`jobs.search.radius.label.${locale}`];
      div1.append(label1, input1);
      div2.append(label2, input2);
      div3.append(label3, input3);
      wrapper.replaceChildren(div1, div2, div3, button);
      insertRef.current = true;
    }
  }, [locale]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const redirectAfterDelay = useCallback(() => {
    const interval = setTimeout(() => {
      if (allowRedirect === true) {
        if (seconds > 0) {
          setSeconds(seconds - 1);
        } else {
          const urlParams = new URLSearchParams({ redirect: 0 });
          window.location.href = `${window.location.pathname}?${urlParams.toString()}`;
        }
      }
    }, 1000);

    return () => {
      clearTimeout(interval);
    };
  }, [seconds, allowRedirect]);

  return (
    <div className={clsx(className, classes.root, { loading: !ready })}>
      <Container className="px-2 px-md-3">
        <JobSearch
          translations={{
            "button.text": placeholders[`jobs.search.button.${locale}`],
            "input.placeHolder": placeholders[`jobs.search.query.placeholder.${locale}`],
            "input.locationPlaceholder": placeholders[`jobs.search.location.placeholder.${locale}`],
            "searchResults.noResultsText": placeholders[`jobs.search.noResults.${locale}`],
            "filters.resetSearchText": (
              <>
                <Reset />
                <span className="ms-2">{placeholders[`jobs.search.resetSearch.${locale}`]}</span>
              </>
            ),
          }}
          options={{
            // searchPagination: {
            //   variant: "paged",
            //   reactPaginationOptions: {
            //     pageRangeDisplayed: 3,
            //     marginPagesDisplayed: 4,
            //     nextLabel: "next >",
            //     previousLabel: "< prev",
            //   },
            // },
            urlFilters: {
              mode: "active",
              jobCategories: [jobsSectors, jobsType, jobsWorkingEnvironment],
              pushToHistoryOnSearch: true,
              changeURLOnFilterChange: true,
            },
            jobsUrl: `/jobs`,
            searchInput: {
              queryInput: true,
              locationInput: true,
              locationRegion: "",
              radiusInput: true,
              radiusOptions: {
                units: "kilometres",
              },
            },
            searchFiltersOptions: {
              filterFoldElement: <CaretDown width={20} height={20} />,
              showResetSearchButton: true,
              topLevelCategoryOrder: [jobsSectors.id, jobsType.id, jobsWorkingEnvironment.id],
              exclude: {
                "84ddafd9-a498-4832-91ab-40d7a04f98ab": [
                  "8126dfcb-af86-472e-b393-35429f22df6e",
                  "ebea240f-8a09-46fe-a852-f584fc303a88",
                  "3bf8d97d-8b96-4e72-8322-34389b16448b",
                  "4e93cf13-778f-440c-90ae-06f8b6b876b8",
                  "4deb0006-5d7a-4a9e-b1c8-d228b613c6d5",
                  "a0b89e0a-de44-4009-ae14-9a42715b88cb",
                  "820254a9-e451-4c42-bd80-5e7ea349fb39",
                  "bade973d-6f63-4a35-b6f5-e6abdf575e0f",
                  "083974f5-c5f8-46c6-812e-6c6187c4b57f",
                  "3ad1e117-cc85-4f59-8306-132143c36354",
                  "033077e0-07ac-4d59-bf56-20cc62e7df6b",
                  "bc1f3905-8c0a-4271-b5b4-a673b71757f2",
                  "2223d548-7228-49d3-a394-8307e1fd9ea5",
                  "5268cdd7-dc25-4067-9ede-2dc2e8aa36aa",
                  "26dab494-6634-4e08-8049-99b2e1130859",
                  "dccd6ace-a994-4e1e-b202-085da47942cd",
                  "2b69a029-55f3-468f-82ae-e6bbcbf4c31e",
                  "0b44acc9-b492-4be6-96b3-a8fcb5b43948",
                  "7b5ecb50-a614-443c-8cc7-2315958a2b45",
                  "7c3b737d-1c44-423e-8862-03ec68c4f93e",
                  "6dce3428-e3f1-4540-8c95-0050806370a6",
                  "91d54082-cb59-49b4-a065-a0f7c9ebfd01",
                  "14a32192-4ed4-4813-b4fc-20ea5a0a0d8c",
                  "d2d928c2-3ed3-45d8-9638-8dca5dcf7c35",
                ],
              },
              filterFoldDepth: 99,
              allowFilterCollapse: true,
            },
            searchResults: ({ results }) => {
              if (!results || results.length === 0) {
                redirectAfterDelay();

                return (
                  <Row>
                    <div className="js-result-none">
                      {allowRedirect
                        ? placeholders[`jobs.search.noResults.withRedirect.${locale}`].replaceAll("{seconds}", seconds)
                        : placeholders[`jobs.search.noResults.${locale}`]}
                    </div>
                  </Row>
                );
              }

              return (
                <Row>
                  {results.map(({ job }, k) => (
                    <Col xs={12} key={job.id} className="mb-4">
                      <Card className={clsx(classes.card, "bg-light")}>
                        <Card.Header>
                          {k < 4 && <i>{placeholders[`job.card.new.${locale}`]}</i>}
                          <Card.Title className="mb-3" dangerouslySetInnerHTML={{ __html: job.title }} />
                          <Stack className="flex-md-row" gap={{ md: 5, xs: 2 }}>
                            <dl className="d-flex flex-row mb-0">
                              <dt className="me-2">
                                <DynamicText path={`job.card.salary.label.${locale}`} />:
                              </dt>
                              <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.salary_package }} />
                            </dl>
                            <dl className="d-flex flex-row mb-0">
                              <dt className="me-2">
                                <DynamicText path={`job.card.location.label.${locale}`} />:
                              </dt>
                              <dd className="mb-0" dangerouslySetInnerHTML={{ __html: job.addresses[0] }} />
                            </dl>
                          </Stack>
                        </Card.Header>
                        <Card.Body>
                          <Card.Text dangerouslySetInnerHTML={{ __html: trimText(job.description, 148) }} />
                        </Card.Body>
                        <Card.Footer className="d-flex flex-row justify-content-between align-items-center">
                          <a href={link("jobs.item", job.url_slug, "#apply")}>
                            <DynamicText path={`job.card.apply.${locale}`} />
                          </a>
                          <Button variant="outline-primary" href={link("jobs.item", job.url_slug)}>
                            <DynamicText path={`job.card.button.${locale}`} />
                          </Button>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>
              );
            },
          }}
        />
      </Container>
    </div>
  );
};
